import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Fa from 'react-fontawesome';
import { connect } from 'react-redux';
import { Modal, Button, MenuItem } from 'react-bootstrap';
import { delay } from 'lodash';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';

// Import Actions
import { requestSyncOrganization } from '../../modules/Setup/SetupActions';
import { getHideSyncToast, getShouldShowSyncToast } from '../../modules/App/AppReducer';
import { unhideSyncToast } from '../../modules/App/AppActions';
import SyncProgress from '../required-sync/progress';
import DateTimeDisplay from '../date-time-display';

// Import Selectors
import { getUser } from '../../modules/Auth/AuthReducer';

class OrganizationResync extends Component {
  constructor(props) {
    super(props);
    this.state = {
      show: props.showConfirm,
    };
    this.syncRequest = this.syncRequest.bind(this);
    this.done = this.done.bind(this);
    this.handleClick = this.handleClick.bind(this);
    if (props.showConfirm) {
      this.handleClick();
    }
    this.el = typeof document !== 'undefined' ? document.createElement('div') : null;
    this.appRoot = typeof document !== 'undefined' ? document.getElementById('app') || document.body : null;
  }

  componentDidMount() {
    this.mounted = true;
    if (this.appRoot) {
      this.appRoot.appendChild(this.el);
    }
    if (this.props.autoSync && !this.synced) {
      this.synced = true;
      this.syncRequest();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.appRoot) {
      this.appRoot.removeChild(this.el);
    }
    this.el = null;
  }
  
  syncRequest() {
    const { organization } = this.props;
    const syncing = organization && organization.syncing;
    if (syncing) {
      return;
    }
    this.props.dispatch(requestSyncOrganization(organization._id))
      .then(
        () => {
        },
        (e) => {
          this.props.onError(e.data); // to prevent auto sync run in loop
        },
      );
    delay(() => {
      this.setState({ show: false });
      this.props.onClose();
    }, 100);
  }

  done() {
    delay(() => {
      this.setState({ show: false });
      this.props.onClose();
    }, 100);
  }

  handleClick(event) {
    const { isInitial, disabled, organization } = this.props;
    const syncing = organization && organization.syncing === true;
    if (event) {
      event.preventDefault();
    }
    if (disabled || syncing) {
      return;
    }
    this.setState({ show: true });
    if (isInitial) {
      this.syncRequest();
    }
  }

  renderModal() {
    if (!this.el) {
      return null;
    }
    const { show } = this.state;
    const { user, organization } = this.props;
    if (!user._id) {
      return null;
    }

    const canSync = organization._id !== undefined;
    if (!canSync) {
      return null;
    }

    const lastSync = organization && typeof organization.last_synced_date === 'string' ? moment.tz(organization.last_synced_date, moment.tz.guess()) : null;
    const syncTime = organization.last_synced_duration ? moment.duration(organization.last_synced_duration, 'seconds').humanize() : null;

    const el = (
      <Modal show={show} onHide={this.done}>
        <Modal.Header>
          Perform incremental CRM sync now?
        </Modal.Header>
        {!canSync &&
          <div>
            <Modal.Body>
              This organization does not have a valid user connected to it yet.
            </Modal.Body>
            <Modal.Footer>
              <Button bsStyle="primary" onClick={this.done}>Cancel</Button>
            </Modal.Footer>
          </div>
        }
        {canSync &&
          <div>
            <Modal.Body>
              <div>
                <p>
                  Performing an incremental sync now will trigger TruVoice Automation to perform an incremental sync with your
                  CRM to and update TruVoice Automation now{lastSync ? ' with any changes that have occurred since ' : ''}
                  <DateTimeDisplay value={lastSync} format="shortWithTime" showTimezone />.
                  {syncTime ? `This incremental sync process will take approximately ${syncTime} to complete.` : ''}
                </p>
                <p>
                  This will also trigger your reminder emails and automation to be processed based on their current
                  settings, once the incremental sync is completed.
                </p>
                <p />
                <p>Your data was previously synced <strong><DateTimeDisplay value={lastSync} format="shortWithTime" relative /></strong> (<DateTimeDisplay value={lastSync} format="shortWithTime" />)</p>
                {organization.next_sync_date !== null && <p>Scheduled next sync time in <strong><DateTimeDisplay value={organization.next_sync_date} format="shortWithTime" relative /></strong> (<DateTimeDisplay value={organization.next_sync_date} format="shortWithTime" />)</p>}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="row">
                <div className="col-xs-6">
                  <div className="clearfix">
                    <Button className="pull-left" bsStyle="link" onClick={this.done}>Cancel</Button>
                  </div>
                </div>
                <div className="col-xs-6">
                  <div className="clearfix">
                    <Button className="pull-right" bsStyle="primary" onClick={this.syncRequest}>Yes - Refresh database</Button>
                  </div>
                </div>
              </div>
            </Modal.Footer>
          </div>
        }
      </Modal>
    );

    return ReactDOM.createPortal(el, this.el);
  }

  render() {
    const {
      btnTitle, isMenu, className, disabled, organization,
      syncToasterIsHiding, shouldShowSyncToast, autoSync,
    } = this.props;
    if (organization && organization.is_demo) {
      return null;
    }
    const syncing = organization && organization.syncing === true;

    if (isMenu) {
      if (syncing) {
        if (shouldShowSyncToast && syncToasterIsHiding) {
          return [
            (<MenuItem key="d1" divider />),
            (<MenuItem key="h" header>Syncing:</MenuItem>),
            (
              <MenuItem key="s" onClick={(e) => { this.props.dispatch(unhideSyncToast()); }}>
                <SyncProgress mini />
              </MenuItem>
            ),
          ];
        }

        return (
          <MenuItem disabled={(!syncToasterIsHiding || !shouldShowSyncToast)} onClick={(e) => { this.props.dispatch(unhideSyncToast()); }}>
            <Fa name="refresh" spin /> Syncing
          </MenuItem>
        );
      }
      return (
        <MenuItem disabled={disabled} onClick={(e) => { this.handleClick(e); }}>
          <Fa name="refresh" /> {btnTitle}
          {this.renderModal()}
        </MenuItem>
      );
    }

    if (autoSync) {
      return null;
    }

    return (
      <span>
        <a
          disabled={disabled || syncing}
          className={className}
          onClick={(e) => { this.handleClick(e); }}
        >
          {!syncing && <span><Fa name="refresh" /> {btnTitle}</span>}
          {syncing && <span><Fa name="refresh" spin={syncing} /> Syncing</span>}
        </a>
        {this.renderModal()}
      </span>
    );
  }
}

OrganizationResync.propTypes = {
  dispatch: PropTypes.func.isRequired,
  organization: PropTypes.shape({
    _id: PropTypes.string,
    display_name: PropTypes.string,
    syncing: PropTypes.bool,
    is_demo: PropTypes.bool,
    last_synced_date: PropTypes.string,
    last_synced_duration: PropTypes.number,
  }).isRequired,
  btnTitle: PropTypes.string,
  user: PropTypes.shape({
    _id: PropTypes.string,
    user_id: PropTypes.string,
  }).isRequired,
  isInitial: PropTypes.bool,
  isMenu: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  showConfirm: PropTypes.bool,
  onClose: PropTypes.func,
  syncToasterIsHiding: PropTypes.bool,
  shouldShowSyncToast: PropTypes.bool,
  autoSync: PropTypes.bool,
  onError: PropTypes.func,
};
OrganizationResync.defaultProps = {
  btnTitle: 'Refresh',
  isInitial: false,
  isMenu: false,
  disabled: false,
  className: 'btn btn-primary',
  showConfirm: false,
  onClose: () => {},
  syncToasterIsHiding: false, 
  shouldShowSyncToast: false,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  autoSync: false,
  onError: () => {},
};
OrganizationResync.contextTypes = {
  addNotification: PropTypes.func.isRequired,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    user: getUser(state),
    syncToasterIsHiding: getHideSyncToast(state),
    shouldShowSyncToast: getShouldShowSyncToast(state),
  };
}

export default withRouter(connect(mapStateToProps)(OrganizationResync));
